import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Pencil, User, Heart, FileText, Users as UsersIcon, Briefcase, Phone, Mail, MapPin, Calendar, CreditCard, AlertCircle, Pill, Stethoscope, Shield, Trash2 } from 'lucide-react';
import Avatar from '../components/Avatar';
import api, { formatApiError } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { AVATAR_COLORS, formatDate, shortDate, yearFromDate, DOC_TYPES, CONTACT_CATEGORIES } from '../lib/family';

const TABS = [
    { key: 'personal', label: 'Personal', icon: User },
    { key: 'salud', label: 'Salud', icon: Heart },
    { key: 'documentos', label: 'Docs', icon: FileText },
    { key: 'contactos', label: 'Contactos', icon: UsersIcon },
    { key: 'laboral', label: 'Laboral', icon: Briefcase },
];

export default function MemberDetail() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { memberId } = useParams();
    const [params, setParams] = useSearchParams();
    const [member, setMember] = useState(null);
    const [error, setError] = useState('');
    const tab = params.get('tab') || 'personal';

    useEffect(() => {
        api.get(`/family/members/${memberId}`)
            .then((r) => setMember(r.data.member))
            .catch((e) => setError(formatApiError(e)));
    }, [memberId]);

    if (error) {
        return (
            <div className="min-h-screen bg-surface app-shell px-6 py-8">
                <button onClick={() => navigate(-1)} className="text-brand-navy mb-4">← Volver</button>
                <div className="bg-red-50 border border-red-200 rounded-2xl p-4 text-red-700">{error}</div>
            </div>
        );
    }
    if (!member) {
        return <div className="min-h-screen flex items-center justify-center"><div className="w-10 h-10 border-2 border-brand/30 border-t-brand rounded-full animate-spin" /></div>;
    }

    const palette = AVATAR_COLORS[member.avatar_color] || AVATAR_COLORS.pink;
    const isOwn = member.id === user?.id;
    const isAdmin = user?.family_role === 'admin';
    const canEdit = isOwn || member.profile_type === 'informative' || (isAdmin && member.family_role !== 'admin');

    const setTab = (t) => setParams({ tab: t });

    return (
        <div className="min-h-screen bg-surface app-shell pb-16">
            {/* Header banner with member color */}
            <div className="relative pt-4 pb-8 px-4" style={{ background: `linear-gradient(180deg, ${palette.bg} 0%, ${palette.bg}80 100%)` }} data-testid="member-detail-header">
                <div className="flex items-center justify-between">
                    <button onClick={() => navigate('/familia')} className="w-10 h-10 rounded-full bg-white/80 hover:bg-white flex items-center justify-center" data-testid="member-back-btn">
                        <ArrowLeft className="w-5 h-5 text-brand-navy" />
                    </button>
                    <h1 className="text-base font-bold text-brand-navy">Miembros</h1>
                    {canEdit ? (
                        <button onClick={() => navigate(`/familia/miembro/${memberId}/editar?tab=${tab}`)} className="w-10 h-10 rounded-full bg-white/80 hover:bg-white flex items-center justify-center" data-testid="member-edit-btn">
                            <Pencil className="w-5 h-5 text-brand-navy" />
                        </button>
                    ) : <div className="w-10" />}
                </div>

                <div className="flex flex-col items-center mt-6">
                    <Avatar name={member.name} color={member.avatar_color} photo={member.avatar_url} size={100} ring />
                    <h2 className="mt-3 text-2xl font-bold text-brand-navy">{member.name || '(Sin nombre)'}</h2>
                    <div className="flex items-center gap-2 mt-1">
                        <span className="text-sm text-gray-600 font-medium">{member.family_role_label || member.family_role || ''}</span>
                        {member.profile_type === 'informative' && (
                            <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded-full bg-white/70 text-gray-600">Informativo</span>
                        )}
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div className="px-4 -mt-4 mb-4">
                <div className="bg-white shadow-card rounded-full p-1 flex" data-testid="member-tabs">
                    {TABS.map((t) => (
                        <button
                            key={t.key}
                            onClick={() => setTab(t.key)}
                            className={`flex-1 h-10 rounded-full text-[11px] font-bold flex items-center justify-center gap-1 transition ${tab === t.key ? 'bg-brand text-white' : 'text-gray-500 hover:text-brand-navy'}`}
                            data-testid={`member-tab-${t.key}`}
                        >
                            <t.icon className="w-3.5 h-3.5" />
                            <span className="hidden sm:inline">{t.label}</span>
                        </button>
                    ))}
                </div>
            </div>

            <div className="px-4">
                {tab === 'personal' && <PersonalTab member={member} />}
                {tab === 'salud' && <HealthTab member={member} />}
                {tab === 'documentos' && <DocumentsTab member={member} />}
                {tab === 'contactos' && <ContactsTab member={member} />}
                {tab === 'laboral' && <WorkTab member={member} />}
            </div>
        </div>
    );
}

function Row({ icon: Icon, label, value }) {
    return (
        <div className="flex items-start gap-3 py-3 border-b border-gray-100 last:border-0">
            <div className="w-10 h-10 rounded-2xl bg-brand/10 flex items-center justify-center flex-shrink-0">
                <Icon className="w-5 h-5 text-brand" />
            </div>
            <div className="flex-1 min-w-0">
                <div className="text-[11px] text-gray-500 font-semibold uppercase tracking-wide">{label}</div>
                <div className="text-sm text-brand-navy font-medium break-words">{value || <span className="text-gray-400 italic font-normal">No registrado</span>}</div>
            </div>
        </div>
    );
}

function Section({ title, children, testId }) {
    return (
        <div className="bg-white rounded-2xl shadow-card p-5 mb-4" data-testid={testId}>
            <h3 className="text-sm font-bold text-brand-navy mb-2 uppercase tracking-wide">{title}</h3>
            {children}
        </div>
    );
}

function PersonalTab({ member }) {
    const p = member.personal || {};
    return (
        <Section title="Información Personal" testId="tab-personal">
            <Row icon={User} label="Nombre completo" value={p.full_name} />
            <Row icon={Calendar} label="Fecha de nacimiento" value={formatDate(p.birthdate)} />
            <Row icon={CreditCard} label="Número de identidad" value={p.id_number} />
            <Row icon={Phone} label="Teléfono" value={p.phone} />
            <Row icon={Mail} label="Correo" value={p.email} />
            <Row icon={MapPin} label="Dirección" value={p.address} />
        </Section>
    );
}

function HealthTab({ member }) {
    const h = member.health || {};
    if (!h) return <Section title="Información de Salud"><p className="text-sm text-gray-500">Esta información está marcada como privada.</p></Section>;
    return (
        <>
            <Section title="Información de Salud" testId="tab-salud">
                <Row icon={Heart} label="Tipo de sangre" value={h.blood_type} />
            </Section>

            <Section title="Alergias" testId="tab-salud-alergias">
                {(h.allergies || []).length ? (
                    <div className="flex flex-wrap gap-2">
                        {h.allergies.map((a) => (
                            <span key={a.id} className="px-3 py-1.5 bg-rose-100 text-rose-700 rounded-full text-sm font-medium" data-testid={`allergy-${a.id}`}>{a.name}</span>
                        ))}
                    </div>
                ) : <EmptyState text="Sin alergias registradas" />}
            </Section>

            <Section title="Medicamentos" testId="tab-salud-medicamentos">
                {(h.medications || []).length ? (
                    <div className="space-y-2">
                        {h.medications.map((m) => (
                            <div key={m.id} className="flex items-center gap-3 p-3 bg-amber-50 rounded-2xl" data-testid={`medication-${m.id}`}>
                                <Pill className="w-5 h-5 text-amber-600" />
                                <div>
                                    <div className="text-sm font-bold text-amber-900">{m.name}</div>
                                    {m.dose && <div className="text-xs text-amber-700">{m.dose}</div>}
                                </div>
                            </div>
                        ))}
                    </div>
                ) : <EmptyState text="Sin medicamentos" />}
            </Section>

            <Section title="Doctores" testId="tab-salud-doctores">
                {(h.doctors || []).length ? (
                    <div className="space-y-2">
                        {h.doctors.map((d) => (
                            <div key={d.id} className="flex items-center gap-3 p-3 bg-emerald-50 rounded-2xl" data-testid={`doctor-${d.id}`}>
                                <Stethoscope className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-bold text-emerald-900">{d.name}</div>
                                    {d.specialty && <div className="text-xs text-emerald-700">{d.specialty}</div>}
                                    {d.phone && <div className="text-xs text-emerald-600 mt-0.5">{d.phone}</div>}
                                </div>
                            </div>
                        ))}
                    </div>
                ) : <EmptyState text="Sin doctores registrados" />}
            </Section>

            {h.insurance && (h.insurance.company || h.insurance.policy_number) && (
                <Section title="Seguro Médico" testId="tab-salud-seguro">
                    <div className="p-4 bg-blue-50 rounded-2xl">
                        <div className="flex items-center gap-3 mb-2">
                            <Shield className="w-5 h-5 text-blue-600" />
                            <div className="text-sm font-bold text-blue-900">{h.insurance.company || '—'}</div>
                        </div>
                        {h.insurance.policy_number && <div className="text-xs text-blue-700">Póliza: {h.insurance.policy_number}</div>}
                        {h.insurance.coverage && <div className="text-xs text-blue-700">Cobertura: {h.insurance.coverage}</div>}
                    </div>
                </Section>
            )}
        </>
    );
}

function DocumentsTab({ member }) {
    const docs = member.documents || [];
    if (!docs.length) return <Section title="Documentos"><EmptyState text="Sin documentos" /></Section>;

    const today = new Date();
    return (
        <Section title="Documentos" testId="tab-documentos">
            <div className="space-y-2">
                {docs.map((d) => {
                    let warning = null;
                    if (d.expires_at) {
                        const exp = new Date(d.expires_at);
                        const days = Math.round((exp - today) / 86400000);
                        if (days < 0) warning = { label: 'VENCIDO', color: 'bg-red-100 text-red-700' };
                        else if (days <= 30) warning = { label: `Vence en ${days}d`, color: 'bg-orange-100 text-orange-700' };
                    }
                    const typeLabel = DOC_TYPES.find((t) => t.value === d.doc_type)?.label || d.doc_type;
                    return (
                        <div key={d.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-2xl" data-testid={`document-${d.id}`}>
                            <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center flex-shrink-0">
                                <FileText className="w-5 h-5 text-brand" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                    <div className="text-sm font-bold text-brand-navy truncate">{d.name}</div>
                                    {warning && <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${warning.color}`}>{warning.label}</span>}
                                </div>
                                <div className="text-xs text-gray-500 mt-0.5">
                                    <span className="px-2 py-0.5 bg-brand/10 rounded-full text-brand-navy font-medium mr-2">{typeLabel}</span>
                                    {d.number && <span>{d.number}</span>}
                                </div>
                                {d.expires_at && <div className="text-xs text-gray-500 mt-0.5">Vence: {shortDate(d.expires_at)}</div>}
                            </div>
                        </div>
                    );
                })}
            </div>
        </Section>
    );
}

function ContactsTab({ member }) {
    const contacts = member.contacts || [];
    if (!contacts.length) return <Section title="Contactos importantes"><EmptyState text="Sin contactos registrados" /></Section>;
    const grouped = contacts.reduce((acc, c) => { (acc[c.category] = acc[c.category] || []).push(c); return acc; }, {});
    return (
        <>
            {Object.entries(grouped).map(([cat, items]) => {
                const catLabel = CONTACT_CATEGORIES.find((c) => c.value === cat)?.label || cat;
                return (
                    <Section key={cat} title={catLabel} testId={`tab-contactos-${cat}`}>
                        <div className="space-y-2">
                            {items.map((c) => (
                                <div key={c.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-2xl" data-testid={`contact-${c.id}`}>
                                    <Avatar name={c.name} color="teal" size={40} />
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-bold text-brand-navy">{c.name}</div>
                                        {c.relation && <div className="text-[11px] text-gray-500">{c.relation}</div>}
                                    </div>
                                    {c.phone && (
                                        <a href={`tel:${c.phone}`} className="w-10 h-10 bg-emerald-100 hover:bg-emerald-200 rounded-full flex items-center justify-center" data-testid={`contact-${c.id}-call`}>
                                            <Phone className="w-4 h-4 text-emerald-600" />
                                        </a>
                                    )}
                                </div>
                            ))}
                        </div>
                    </Section>
                );
            })}
        </>
    );
}

function WorkTab({ member }) {
    const w = member.work || {};
    return (
        <Section title="Información Laboral" testId="tab-laboral">
            <Row icon={Briefcase} label="Empresa" value={w.company} />
            <Row icon={User} label="Puesto" value={w.position} />
            <Row icon={User} label="Jefe inmediato" value={w.supervisor} />
            <Row icon={Phone} label="Teléfono de trabajo" value={w.work_phone} />
            <Row icon={Mail} label="Correo corporativo" value={w.work_email} />
            <Row icon={MapPin} label="Dirección de oficina" value={w.office_address} />
        </Section>
    );
}

function EmptyState({ text }) {
    return <p className="text-sm text-gray-400 italic">{text}</p>;
}
